import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useCanvasStore, Item } from '@/stores/canvasStore';
import { usePartnrStore } from '@/stores/partnrStore';
import { usePartnrBetaStore } from '@/stores/partnrBetaStore';
import { supabase } from '@/integrations/supabase/client';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Search, 
  Sparkles, 
  Plus, 
  ExternalLink, 
  X, 
  StickyNote, 
  Type, 
  FileText, 
  Image, 
  File,
  History,
  Send,
  Loader2,
  Link as LinkIcon,
  ArrowRight,
} from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';
import { PartnrChatHistory } from './PartnrChatHistory';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { SyncChoiceModal } from '@/components/canvas/SyncChoiceModal';

interface SearchResult extends Item {
  boardName?: string;
  boardColor?: string;
}

/** Detect if a query looks like a question or AI request rather than a search term */
function isQuestionLikeQuery(query: string): boolean {
  const trimmed = query.trim().toLowerCase();
  if (trimmed.length < 5) return false;
  
  // Starts with question words
  const questionStarts = [
    'what ', 'how ', 'why ', 'when ', 'where ', 'who ', 'which ',
    'can ', 'could ', 'should ', 'would ', 'is ', 'are ', 'do ', 'does ',
    'help ', 'explain ', 'summarize ', 'compare ', 'analyze ', 'create ',
    'write ', 'generate ', 'make ', 'find connections', 'brainstorm',
    'tell me', 'show me', 'give me',
  ];
  
  if (questionStarts.some(q => trimmed.startsWith(q))) return true;
  
  // Ends with question mark
  if (trimmed.endsWith('?')) return true;
  
  // Contains phrases that suggest AI intent
  const aiPhrases = ['ideas for', 'suggestions for', 'between these', 'related to'];
  if (aiPhrases.some(p => trimmed.includes(p))) return true;
  
  return false;
}

export function PartnrSearchBar() {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<SearchResult[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [selectedItem, setSelectedItem] = useState<SearchResult | null>(null);
  const [isResultsOpen, setIsResultsOpen] = useState(false);
  const [syncModalState, setSyncModalState] = useState<{
    item: SearchResult;
    position: { x: number; y: number };
  } | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  const { 
    boards, 
    currentBoardId, 
    createItem, 
    panX, 
    panY, 
    zoom,
    setViewMode,
    navigateToBoard,
    fetchBoards,
    items,
    selectItem,
    clearSelection,
    setPan,
    selectedItemIds: canvasSelectedItemIds,
  } = useCanvasStore();

  const {
    isPartnrMode,
    setPartnrMode,
    isHistoryOpen,
    toggleHistoryPanel,
    createChat,
    isStreaming,
    partnrSelectedItemIds,
    clearPartnrSelection,
    togglePartnrItemSelection,
    setSearchEscalationContext,
    searchEscalationContext,
  } = usePartnrStore();

  // Get Partnr-selected items
  const selectedItems = items.filter(item => partnrSelectedItemIds.has(item.id));
  
  // Partnr beta state
  const { isPartnrBetaEnabled } = usePartnrBetaStore();
  
  // Check if canvas has selected items (for glow effect) - only when beta is enabled
  const hasCanvasSelection = isPartnrBetaEnabled && canvasSelectedItemIds.size > 0;

  // Dynamic suggestions based on escalation context
  const escalationSuggestions = useMemo(() => {
    if (!searchEscalationContext) return null;
    
    const { originalQuery, resultCount } = searchEscalationContext;
    
    if (resultCount === 0) {
      return [
        `Help me find information about "${originalQuery}"`,
        `What do you know about ${originalQuery}?`,
        `Suggest related topics to ${originalQuery}`,
        `Help me create content about ${originalQuery}`,
      ];
    }
    
    return [
      `Summarize what I found about "${originalQuery}"`,
      `Find connections between these results`,
      `What's missing from my research on "${originalQuery}"?`,
      `Turn these findings into an action plan`,
    ];
  }, [searchEscalationContext]);

  // Helper to get item preview text
  const getItemPreviewText = (item: Item): string => {
    const content = item.content as Record<string, unknown>;
    switch (item.item_type) {
      case 'sticky':
        return (content.text as string)?.substring(0, 20) || 'Sticky Note';
      case 'text_card':
        return (content.title as string)?.substring(0, 20) || 'Text Card';
      case 'document':
        return (content.title as string)?.substring(0, 20) || 'Document';
      case 'image':
        return (content.alt as string)?.substring(0, 20) || 'Image';
      case 'file':
        return (content.filename as string)?.substring(0, 20) || 'File';
      case 'link':
        return (content.title as string)?.substring(0, 20) || 'Link';
      default:
        return 'Item';
    }
  };

  // Fetch boards on mount
  useEffect(() => {
    fetchBoards();
  }, [fetchBoards]);

  // Close on escape
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setIsResultsOpen(false);
        setSelectedItem(null);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  // Close on click outside
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setIsResultsOpen(false);
        setSelectedItem(null);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Search function with fuzzy matching and scoring
  const searchItems = useCallback(async (searchQuery: string) => {
    if (!searchQuery.trim()) {
      setResults([]);
      return;
    }

    setIsSearching(true);
    
    const { data, error } = await supabase
      .from('items')
      .select('*')
      .order('created_at', { ascending: false });

    if (error) {
      console.error('Error searching items:', error);
      setIsSearching(false);
      return;
    }

    const q = searchQuery.toLowerCase().trim();
    const queryWords = q.split(/\s+/).filter(w => w.length > 1);

    // Score each item based on multiple relevance signals
    const scored = (data || []).map(item => {
      if (item.item_type === 'board_link') return { item, score: 0 };
      
      const content = item.content as Record<string, unknown>;
      const fields = [
        (content.text as string || ''),
        (content.title as string || ''),
        (content.body as string || '').replace(/<[^>]*>/g, ''), // Strip HTML
        (content.filename as string || ''),
        (content.alt as string || ''),
        (content.description as string || ''),
        (content.url as string || ''),
        (content.siteName as string || ''),
        (content.aiDescription as string || ''),
        (content.analysis as string || ''),
      ].map(f => f.toLowerCase());

      const fullText = fields.join(' ');
      let score = 0;

      // Exact phrase match (highest weight)
      if (fullText.includes(q)) {
        score += 100;
      }

      // Individual word matches
      for (const word of queryWords) {
        for (let i = 0; i < fields.length; i++) {
          if (fields[i].includes(word)) {
            score += 10;
            // Bonus for title/filename match
            if (i === 1 || i === 3) {
              score += 15;
            }
          }
        }
      }

      // Word-start matches (e.g., "des" matches "design")
      for (const word of queryWords) {
        const wordStartRegex = new RegExp(`\\b${word}`, 'i');
        if (wordStartRegex.test(fullText)) {
          score += 5;
        }
      }

      // Item type relevance boost (recent items score higher)
      const ageMs = Date.now() - new Date(item.created_at).getTime();
      const ageDays = ageMs / (1000 * 60 * 60 * 24);
      if (ageDays < 7) score += 3;
      if (ageDays < 1) score += 5;

      return { item, score };
    });

    // Filter items with score > 0, sort by score descending, limit to 20
    const results = scored
      .filter(s => s.score > 0)
      .sort((a, b) => b.score - a.score)
      .slice(0, 20)
      .map(s => {
        const board = boards.find(b => b.id === s.item.board_id);
        return {
          ...s.item,
          item_type: s.item.item_type as Item['item_type'],
          boardName: board?.name,
          boardColor: board?.icon_color,
        };
      });

    setResults(results);
    setIsSearching(false);
  }, [boards]);

  // Debounced search (only in search mode)
  useEffect(() => {
    if (isPartnrMode) return;
    const timeout = setTimeout(() => {
      searchItems(query);
    }, 200);
    return () => clearTimeout(timeout);
  }, [query, searchItems, isPartnrMode]);

  const getItemIcon = (type: string) => {
    switch (type) {
      case 'sticky': return StickyNote;
      case 'text_card': return Type;
      case 'document': return FileText;
      case 'image': return Image;
      case 'file': return File;
      case 'link': return LinkIcon;
      default: return FileText;
    }
  };

  const stripHtml = (html: string): string => {
    const temp = document.createElement('div');
    temp.innerHTML = html;
    return temp.textContent || temp.innerText || '';
  };

  const getItemPreview = (item: Item): string => {
    const content = item.content as Record<string, unknown>;
    switch (item.item_type) {
      case 'sticky':
        return (content.text as string) || 'Empty sticky note';
      case 'text_card':
        return (content.title as string) || stripHtml((content.body as string) || '') || 'Empty text card';
      case 'document':
        return (content.title as string) || 'Untitled document';
      case 'image':
        return (content.alt as string) || 'Image';
      case 'file':
        return (content.filename as string) || 'File';
      case 'link':
        return (content.title as string) || (content.url as string) || 'Link';
      default:
        return 'Item';
    }
  };

  const addToCanvas = async (item: SearchResult) => {
    if (!currentBoardId) {
      toast.error('Please navigate to a board first');
      return;
    }

    const viewportCenterX = (window.innerWidth / 2) / zoom - panX;
    const viewportCenterY = (window.innerHeight / 2) / zoom - panY;
    const position = {
      x: Math.round(viewportCenterX / 20) * 20,
      y: Math.round(viewportCenterY / 20) * 20,
    };

    // If the item is from a different board, show duplicate/sync choice
    if (item.board_id && item.board_id !== currentBoardId) {
      setSyncModalState({ item, position });
      setSelectedItem(null);
      return;
    }

    const result = await createItem({
      board_id: currentBoardId,
      item_type: item.item_type,
      position_x: position.x,
      position_y: position.y,
      width: item.width,
      height: item.height,
      content: item.content as Record<string, unknown>,
    });

    if (result) {
      toast.success('Added to canvas');
      setIsResultsOpen(false);
      setQuery('');
      setSelectedItem(null);
    }
  };

  const handleSearchDuplicate = async () => {
    if (!syncModalState || !currentBoardId) return;
    const { item, position } = syncModalState;

    await createItem({
      board_id: currentBoardId,
      item_type: item.item_type,
      position_x: position.x,
      position_y: position.y,
      width: item.width,
      height: item.height,
      content: item.content as Record<string, unknown>,
    });

    toast.success('Item duplicated');
    setSyncModalState(null);
    setIsResultsOpen(false);
    setQuery('');
  };

  const handleSearchSync = async () => {
    if (!syncModalState || !currentBoardId) return;
    const { item, position } = syncModalState;

    await createItem({
      board_id: currentBoardId,
      item_type: 'synced_item' as any,
      position_x: position.x,
      position_y: position.y,
      width: item.width,
      height: item.height,
      content: {
        source_item_id: item.id,
        source_board_id: item.board_id,
      },
    });

    toast.success('Synced item created');
    setSyncModalState(null);
    setIsResultsOpen(false);
    setQuery('');
  };

  const goToOriginal = async (item: SearchResult) => {
    const board = boards.find(b => b.id === item.board_id);
    if (board) {
      setViewMode('focus');
      await navigateToBoard(board);
      setIsResultsOpen(false);
      setQuery('');
      setSelectedItem(null);
    }
  };

  const handlePartnrSubmit = async () => {
    if (!query.trim() || !currentBoardId || isStreaming) return;

    const chat = await createChat(currentBoardId, selectedItems, query);
    if (chat) {
      setQuery('');
    }
  };

  // Compute if we should show the Partnr suggestion
  const showPartnrSuggestion = !isPartnrMode && query.trim().length > 4 && isQuestionLikeQuery(query);

  /** Escalate to Partnr using the canvas-selected items as context */
  const handleAskPartnrAboutSelection = () => {
    if (canvasSelectedItemIds.size === 0 || !currentBoardId) return;
    const canvasItems = items.filter(item => canvasSelectedItemIds.has(item.id));
    if (canvasItems.length === 0) return;

    // Transfer canvas selection to Partnr selection
    clearPartnrSelection();
    for (const item of canvasItems.slice(0, 5)) {
      togglePartnrItemSelection(item.id);
    }

    // Switch to Partnr mode and keep the query
    setPartnrMode(true);
    setIsResultsOpen(false);

    // If there's already a question, submit it immediately
    if (query.trim() && isQuestionLikeQuery(query)) {
      setTimeout(() => handlePartnrSubmit(), 0);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    // Escape exits Partnr mode back to search
    if (e.key === 'Escape' && isPartnrMode) {
      e.preventDefault();
      setPartnrMode(false);
      setSearchEscalationContext(null);
      return;
    }

    if (e.key === 'Enter' && isPartnrMode) {
      e.preventDefault();
      handlePartnrSubmit();
      return;
    }

    // Enter with canvas selection and a question-like query → ask Partnr about selection
    if (e.key === 'Enter' && !isPartnrMode && hasCanvasSelection && query.trim()) {
      e.preventDefault();
      handleAskPartnrAboutSelection();
      return;
    }

    // Tab to escalate when showing Partnr suggestion
    if (e.key === 'Tab' && showPartnrSuggestion && !isPartnrMode) {
      e.preventDefault();
      handleEscalateToPartnr(query);
      return;
    }

    // Cmd/Ctrl+Enter escalates to Partnr from search
    if ((e.metaKey || e.ctrlKey) && e.key === 'Enter' && !isPartnrMode && query.trim()) {
      e.preventDefault();
      handleEscalateToPartnr(query);
      return;
    }
  };

  const handleRemoveSelectedItem = (itemId: string) => {
    togglePartnrItemSelection(itemId);
  };

  /** Escalate: switch to Partnr mode with the search query as the prompt */
  const handleEscalateToPartnr = (searchQuery: string) => {
    // Store escalation context
    setSearchEscalationContext({
      originalQuery: searchQuery,
      resultCount: results.length,
      resultItemIds: results.slice(0, 4).map(r => r.id),
    });
    
    // Switch to Partnr mode
    setPartnrMode(true);
    
    // Pre-fill the Partnr input with the search query
    setQuery(searchQuery);

    // Close search results panel
    setIsResultsOpen(false);

    // Don't auto-select items — let the user choose which items to reference
    clearPartnrSelection();
  };

  /** Escalate with results: switch to Partnr mode with current results as context */
  const handleEscalateToPartnrWithResults = () => {
    // Store escalation context
    setSearchEscalationContext({
      originalQuery: query,
      resultCount: results.length,
      resultItemIds: results.slice(0, 4).map(r => r.id),
    });
    
    // Switch to Partnr mode
    setPartnrMode(true);

    // Don't auto-select items — let the user choose which items to reference
    clearPartnrSelection();

    // Clear the input so user can type a new prompt about the results
    setQuery('');

    // Close search results
    setIsResultsOpen(false);
  };

  /** Handle clicking a suggestion chip */
  const handleSuggestionClick = (suggestion: string) => {
    setQuery(suggestion);
    inputRef.current?.focus();
  };

  /** Create a sticky note from search query when no results */
  const handleCreateFromSearch = async (searchQuery: string) => {
    if (!currentBoardId) {
      toast.error('Navigate to a board first');
      return;
    }
    
    const centerX = (window.innerWidth / 2) / zoom - panX;
    const centerY = (window.innerHeight / 2) / zoom - panY;
    
    await createItem({
      board_id: currentBoardId,
      item_type: 'sticky',
      position_x: Math.round(centerX / 20) * 20,
      position_y: Math.round(centerY / 20) * 20,
      width: 200,
      height: 150,
      content: { text: searchQuery },
    });
    
    setIsResultsOpen(false);
    setQuery('');
    toast.success('Created sticky note');
  };

  /** Quick-add search result or scroll to it */
  const handleQuickAddToCanvas = (searchResult: SearchResult) => {
    if (searchResult.board_id === currentBoardId) {
      // Item is on the current board — select it and scroll to it
      clearSelection();
      selectItem(searchResult.id, false);
      
      // Pan to center the item in viewport
      const centerX = -(searchResult.position_x + searchResult.width / 2) + (window.innerWidth / 2) / zoom;
      const centerY = -(searchResult.position_y + searchResult.height / 2) + (window.innerHeight / 2) / zoom;
      setPan(centerX, centerY);
      
      setIsResultsOpen(false);
      setQuery('');
      toast.success('Scrolled to item');
    } else {
      // Item is on a different board — add to Partnr selection
      togglePartnrItemSelection(searchResult.id);
      toast.info('Added to Partnr context');
    }
  };

  // Dynamic placeholder based on search context
  const partnrPlaceholder = searchEscalationContext
    ? `Ask about "${searchEscalationContext.originalQuery.substring(0, 30)}${searchEscalationContext.originalQuery.length > 30 ? '...' : ''}"...`
    : "Ask Partnr anything...";

  /** Handle mode toggle with proper state cleanup */
  const handleModeToggle = () => {
    if (isPartnrMode) {
      // Switching back to search mode
      setPartnrMode(false);
      setSearchEscalationContext(null);
      clearPartnrSelection();
    } else {
      // Switching to Partnr mode manually (not via escalation)
      setPartnrMode(true);
      setIsResultsOpen(false);
      setQuery('');
    }
  };

  return (
    <TooltipProvider>
      <div ref={containerRef} className="relative">
        <div className={cn(
          "relative flex items-center gap-1 rounded-full transition-all duration-300",
          isPartnrMode && "partnr-glow ring-1 ring-partnr/30",
          isPartnrMode && selectedItems.length > 0 && "partnr-glow-active",
          // Purple glow when canvas items are selected (indicating Partnr can be activated)
          !isPartnrMode && hasCanvasSelection && "ring-2 ring-partnr shadow-[0_0_20px_rgba(139,92,246,0.4)]"
        )}>
          {/* Icon with gradient background when items selected */}
          <div className={cn(
            "relative flex items-center justify-center h-9 w-9 rounded-full transition-all duration-300",
            isPartnrMode 
              ? "bg-partnr text-white shadow-partnr partnr-glow-active" 
              : hasCanvasSelection
                ? "text-white"
                : "bg-transparent text-muted-foreground"
          )}
          style={hasCanvasSelection && !isPartnrMode ? {
            background: 'linear-gradient(135deg, hsl(292 91% 73%), hsl(270 91% 65%))'
          } : undefined}
          >
            {isPartnrMode ? (
              <Sparkles className="h-4 w-4" />
            ) : hasCanvasSelection ? (
              <Sparkles className="h-4 w-4" />
            ) : (
              <Search className="h-4 w-4" />
            )}
            {/* Selection count badge when canvas items are selected */}
            {!isPartnrMode && hasCanvasSelection && (
              <span className="absolute -top-1.5 -right-1.5 w-4 h-4 rounded-full bg-partnr-secondary text-[10px] text-white flex items-center justify-center font-bold shadow-sm">
                {canvasSelectedItemIds.size}
              </span>
            )}
            {/* Search context indicator */}
            {searchEscalationContext && isPartnrMode && (
              <span className="absolute -top-0.5 -right-0.5 w-2.5 h-2.5 rounded-full bg-partnr-secondary border-2 border-background" />
            )}
          </div>

          {/* Input Field with AnimatePresence */}
          <AnimatePresence mode="wait">
            {isPartnrMode ? (
              <motion.div
                key="partnr-mode"
                initial={{ opacity: 0, x: -8 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: 8 }}
                transition={{ duration: 0.2 }}
                className="relative flex-1"
              >
                <Input
                  ref={inputRef}
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  onKeyDown={handleKeyDown}
                  placeholder={partnrPlaceholder}
                  className="w-[220px] h-9 text-sm rounded-full border-none pr-16 bg-partnr/5 placeholder:text-muted-foreground/50 focus-visible:ring-1 focus-visible:ring-partnr/30"
                />
                {/* Partnr Action Buttons */}
                <div className="absolute right-2 top-1/2 -translate-y-1/2 flex items-center gap-1">
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <button
                        onClick={toggleHistoryPanel}
                        className={cn(
                          "text-muted-foreground hover:text-partnr p-0.5 transition-colors",
                          isHistoryOpen && "text-partnr"
                        )}
                      >
                        <History className="h-3.5 w-3.5" />
                      </button>
                    </TooltipTrigger>
                    <TooltipContent side="top" className="text-xs">
                      Chat History
                    </TooltipContent>
                  </Tooltip>
                  {query && (
                    <button
                      onClick={handlePartnrSubmit}
                      disabled={isStreaming}
                      className="text-partnr hover:text-partnr/80 p-0.5 transition-colors disabled:opacity-50"
                    >
                      {isStreaming ? (
                        <Loader2 className="h-3.5 w-3.5 animate-spin" />
                      ) : (
                        <Send className="h-3.5 w-3.5" />
                      )}
                    </button>
                  )}
                </div>
              </motion.div>
            ) : (
              <motion.div
                key="search-mode"
                initial={{ opacity: 0, x: 8 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -8 }}
                transition={{ duration: 0.2 }}
                className="relative flex-1"
              >
                <Input
                  ref={inputRef}
                  value={query}
                  onChange={(e) => {
                    setQuery(e.target.value);
                    setIsResultsOpen(true);
                  }}
                  onFocus={() => setIsResultsOpen(true)}
                  onKeyDown={handleKeyDown}
                  placeholder={hasCanvasSelection ? `Ask Partnr about ${canvasSelectedItemIds.size} item${canvasSelectedItemIds.size > 1 ? 's' : ''}...` : "Search your mindfull..."}
                  className="w-[220px] h-9 text-sm rounded-full border-none pr-8 bg-background/50"
                />
                {/* Search Action Buttons */}
                <div className="absolute right-2 top-1/2 -translate-y-1/2 flex items-center gap-1">
                  {query && (
                    <button
                      onClick={() => {
                        setQuery('');
                        setResults([]);
                      }}
                      className="text-muted-foreground hover:text-foreground p-0.5"
                    >
                      <X className="h-3.5 w-3.5" />
                    </button>
                  )}
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        {/* Selected Items Pills (Partnr mode) */}
        {isPartnrMode && selectedItems.length > 0 && (
          <div className="absolute bottom-full left-0 mb-3 flex flex-wrap gap-1.5 max-w-[360px]">
            {selectedItems.map((item, index) => (
              <div 
                key={item.id}
                className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium bg-partnr-static text-white shadow-sm"
              >
                <span 
                  className="w-4 h-4 rounded-full bg-white/90 text-[10px] flex items-center justify-center font-bold"
                  style={{ color: 'hsl(var(--partnr))' }}
                >
                  {index + 1}
                </span>
                <span className="max-w-[100px] truncate">{getItemPreviewText(item)}</span>
                <button 
                  onClick={() => handleRemoveSelectedItem(item.id)}
                  className="hover:text-white/70"
                >
                  <X className="h-3 w-3" />
                </button>
              </div>
            ))}
            <button
              onClick={clearPartnrSelection}
              className="px-2 py-1 rounded-full text-xs text-muted-foreground hover:text-foreground hover:bg-muted"
            >
              Clear all
            </button>
          </div>
        )}

        {/* Escalation Suggestions (Partnr mode with search context) */}
        {isPartnrMode && escalationSuggestions && partnrSelectedItemIds.size > 0 && (
          <div className="absolute bottom-full left-0 mb-14 w-[360px]">
            <div className="bg-popover/95 backdrop-blur-sm border border-border/50 rounded-lg shadow-lg px-3 py-2.5">
              <p className="text-[10px] text-muted-foreground/60 mb-1.5 uppercase tracking-wider font-medium">
                Based on your search
              </p>
              <div className="flex flex-wrap gap-1">
                {escalationSuggestions.map((suggestion) => (
                  <button
                    key={suggestion}
                    onClick={() => handleSuggestionClick(suggestion)}
                    className="text-[11px] px-2.5 py-1 rounded-full border border-partnr/20 hover:border-partnr/40 hover:bg-partnr/5 text-muted-foreground hover:text-foreground transition-all truncate max-w-[200px]"
                  >
                    {suggestion}
                  </button>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Search Results Panel */}
        {!isPartnrMode && isResultsOpen && (query || results.length > 0 || hasCanvasSelection) && (
          <div className="absolute bottom-full left-0 mb-2 w-[360px] max-h-[400px] overflow-auto bg-popover border border-border rounded-lg shadow-lg">
            {/* Ask Partnr about canvas-selected items */}
            {hasCanvasSelection && (
              <button
                onClick={handleAskPartnrAboutSelection}
                className="w-full flex items-center gap-3 px-4 py-3 bg-gradient-to-r from-violet-600/10 to-purple-600/10 hover:from-violet-600/20 hover:to-purple-600/20 border-b border-partnr/10 transition-colors group"
              >
                <div className="w-8 h-8 rounded-full bg-partnr/20 flex items-center justify-center shrink-0 group-hover:bg-partnr/30 transition-colors">
                  <Sparkles className="h-4 w-4 text-partnr" />
                </div>
                <div className="flex-1 text-left min-w-0">
                  <p className="text-sm font-medium text-foreground">
                    Ask Partnr about {canvasSelectedItemIds.size === 1 ? 'this item' : `these ${canvasSelectedItemIds.size} items`}
                  </p>
                  <p className="text-xs text-muted-foreground truncate">
                    {query.trim() ? `"${query}"` : 'Type a question or press Enter'}
                  </p>
                </div>
                <ArrowRight className="h-4 w-4 text-partnr opacity-0 group-hover:opacity-100 transition-opacity shrink-0" />
              </button>
            )}

            {/* Partnr Suggestion Banner (question-like query without selection) */}
            {showPartnrSuggestion && !hasCanvasSelection && (
              <button
                onClick={() => handleEscalateToPartnr(query)}
                className="w-full flex items-center gap-3 px-4 py-3 bg-partnr/5 hover:bg-partnr/10 border-b border-partnr/10 transition-colors group"
              >
                <div className="w-8 h-8 rounded-full bg-partnr/15 flex items-center justify-center shrink-0 group-hover:bg-partnr/25 transition-colors">
                  <Sparkles className="h-4 w-4 text-partnr" />
                </div>
                <div className="flex-1 text-left min-w-0">
                  <p className="text-sm font-medium text-foreground">Ask Partnr instead</p>
                  <p className="text-xs text-muted-foreground truncate">
                    "{query}" looks like a question — Partnr can help
                  </p>
                </div>
                <ArrowRight className="h-4 w-4 text-partnr opacity-0 group-hover:opacity-100 transition-opacity shrink-0" />
              </button>
            )}

            {/* When canvas items are selected and query is question-like, suppress search results */}
            {hasCanvasSelection && showPartnrSuggestion ? (
              <div className="px-4 py-3 text-center">
                <p className="text-xs text-muted-foreground">
                  Press <kbd className="px-1.5 py-0.5 rounded bg-muted text-[11px] font-mono">Enter</kbd> to ask Partnr
                </p>
              </div>
            ) : isSearching ? (
              <div className="p-4 text-center text-muted-foreground">
                Searching...
              </div>
            ) : results.length === 0 && query.trim().length > 0 ? (
              <div className="px-4 py-6 text-center">
                <div className="w-10 h-10 rounded-full bg-muted/50 flex items-center justify-center mx-auto mb-3">
                  <Search className="h-5 w-5 text-muted-foreground/50" />
                </div>
                <p className="text-sm text-muted-foreground mb-1">No results for "{query}"</p>
                <div className="flex flex-col items-center gap-2 mt-3">
                  <button
                    onClick={() => hasCanvasSelection ? handleAskPartnrAboutSelection() : handleEscalateToPartnr(query)}
                    className="inline-flex items-center gap-1.5 text-sm text-partnr hover:text-partnr/80 font-medium transition-colors"
                  >
                    <Sparkles className="h-3.5 w-3.5" />
                    {hasCanvasSelection ? `Ask Partnr about ${canvasSelectedItemIds.size} selected item${canvasSelectedItemIds.size > 1 ? 's' : ''}` : 'Ask Partnr about this'}
                  </button>
                  <button
                    onClick={() => handleCreateFromSearch(query)}
                    className="inline-flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground transition-colors"
                  >
                    <StickyNote className="h-3 w-3" />
                    Create a sticky note instead
                  </button>
                </div>
              </div>
            ) : results.length > 0 ? (
              <>
                <div className="p-2 space-y-1">
                  {results.map((item) => {
                    const Icon = getItemIcon(item.item_type);
                    const preview = getItemPreview(item);
                    const content = item.content as Record<string, unknown>;
                    const isOnCurrentBoard = item.board_id === currentBoardId;
                    
                    return (
                      <div
                        key={item.id}
                        className="group flex items-start gap-3 p-2 rounded-md hover:bg-muted cursor-pointer"
                        onClick={() => setSelectedItem(item)}
                      >
                        {item.item_type === 'image' && content.url ? (
                          <div className="w-12 h-12 rounded overflow-hidden shrink-0 bg-muted">
                            <img 
                              src={content.url as string} 
                              alt="" 
                              className="w-full h-full object-cover"
                            />
                          </div>
                        ) : (
                          <div 
                            className={cn(
                              "w-12 h-12 rounded flex items-center justify-center shrink-0",
                              item.item_type === 'sticky' 
                                ? `bg-${(content.color as string) || 'yellow'}-200`
                                : 'bg-muted'
                            )}
                            style={item.item_type === 'sticky' ? {
                              backgroundColor: `var(--sticky-${(content.color as string) || 'yellow'}, hsl(var(--muted)))`
                            } : undefined}
                          >
                            <Icon className="h-5 w-5 text-muted-foreground" />
                          </div>
                        )}

                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium truncate">{preview}</p>
                          {item.boardName && (
                            <div className="flex items-center gap-1.5 mt-0.5">
                              <div 
                                className="w-2 h-2 rounded-full" 
                                style={{ backgroundColor: item.boardColor || 'hsl(var(--primary))' }}
                              />
                              <span className="text-xs text-muted-foreground truncate">
                                {item.boardName}
                              </span>
                            </div>
                          )}
                        </div>

                        <Button
                          size="icon"
                          variant="ghost"
                          className="h-8 w-8 shrink-0 opacity-0 group-hover:opacity-100 transition-opacity"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleQuickAddToCanvas(item);
                          }}
                          title={isOnCurrentBoard ? 'Go to item' : 'Add to Partnr context'}
                        >
                          {isOnCurrentBoard ? (
                            <ExternalLink className="h-4 w-4" />
                          ) : (
                            <Plus className="h-4 w-4" />
                          )}
                        </Button>
                      </div>
                    );
                  })}
                </div>

                {/* Ask Partnr about results footer */}
                <div className="border-t border-border/50 px-4 py-2.5">
                  <button
                    onClick={handleEscalateToPartnrWithResults}
                    className="w-full flex items-center justify-center gap-2 text-xs text-muted-foreground hover:text-partnr transition-colors py-1"
                  >
                    <Sparkles className="h-3 w-3" />
                    <span>Ask Partnr about these {results.length} results</span>
                  </button>
                </div>
              </>
            ) : null}
          </div>
        )}

        {/* Chat History Panel */}
        {isPartnrMode && isHistoryOpen && (
          <PartnrChatHistory />
        )}

        {/* Item Preview Modal */}
        {selectedItem && (
          <div 
            className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50"
            onClick={() => setSelectedItem(null)}
          >
            <div 
              className="bg-popover border border-border rounded-lg shadow-xl max-w-lg w-full mx-4 p-6"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-start justify-between mb-4">
                <div>
                  <h3 className="text-lg font-semibold">{getItemPreview(selectedItem)}</h3>
                  {selectedItem.boardName && (
                    <div className="flex items-center gap-1.5 mt-1">
                      <div 
                        className="w-2 h-2 rounded-full" 
                        style={{ backgroundColor: selectedItem.boardColor || 'hsl(var(--primary))' }}
                      />
                      <span className="text-sm text-muted-foreground">
                        {selectedItem.boardName}
                      </span>
                    </div>
                  )}
                </div>
                <Button variant="ghost" size="icon" onClick={() => setSelectedItem(null)}>
                  <X className="h-4 w-4" />
                </Button>
              </div>

              <div className="mb-6">
                {selectedItem.item_type === 'image' && (selectedItem.content as Record<string, unknown>).url && (
                  <img 
                    src={(selectedItem.content as Record<string, unknown>).url as string}
                    alt=""
                    className="w-full h-auto max-h-[300px] object-contain rounded-md"
                  />
                )}
                {(selectedItem.item_type === 'sticky' || selectedItem.item_type === 'text_card') && (
                  <p className="text-muted-foreground">
                    {(selectedItem.content as Record<string, unknown>).text as string ||
                     stripHtml((selectedItem.content as Record<string, unknown>).body as string || '') ||
                     'No content'}
                  </p>
                )}
                {selectedItem.item_type === 'document' && (
                  <p className="text-muted-foreground line-clamp-6">
                    {stripHtml((selectedItem.content as Record<string, unknown>).body as string || '') || 'No content'}
                  </p>
                )}
              </div>

              <div className="flex gap-2">
                <Button 
                  variant="outline" 
                  className="flex-1"
                  onClick={() => goToOriginal(selectedItem)}
                >
                  <ExternalLink className="h-4 w-4 mr-2" />
                  Go to Original
                </Button>
                <Button 
                  className="flex-1"
                  onClick={() => addToCanvas(selectedItem)}
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Add to Canvas
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Sync Choice Modal for search results from other boards */}
      {syncModalState && (
        <SyncChoiceModal
          isOpen={true}
          onClose={() => setSyncModalState(null)}
          itemName={getItemPreview(syncModalState.item)}
          sourceBoardName={syncModalState.item.boardName || 'another board'}
          onDuplicate={handleSearchDuplicate}
          onSync={handleSearchSync}
        />
      )}
    </TooltipProvider>
  );
}
